import type { ComponentType } from "react";
import Button from "@/components/common/Button";
import SectionHead from "@/components/common/SectionHead";
import RevealStagger from "@/components/common/RevealStagger";
import CountUp from "@/components/common/CountUp";
import Tilt from "@/components/common/Tilt";
import GridBackdrop from "@/components/visual/GridBackdrop";
import {
  ArrowRight,
  ChevronRight,
  AlgoIcon,
  DepthIcon,
  BoltIcon,
  DevicesIcon,
  BeakerIcon,
  LayersIcon,
} from "@/components/common/icons";
import { usePageMeta } from "@/lib/usePageMeta";
import { useLocale } from "@/i18n/useLocale";
import { chromeFor } from "@/content/chrome";
import { platformsFor } from "@/content/platforms";
import type { PlatformFeatureKey, PlatformStatKey, PlatformsContent } from "@/content/types";
import { useBreadcrumbStructuredData } from "@/lib/seo";
import { env } from "@/lib/env";
import { PLATFORM_FAQ_ITEMS } from "@/lib/data";
import { cx } from "@/lib/cx";
import { pathFor } from "@/routes/manifest";

const FAQ_BASE_URL = env.traderFaqUrl;

// The headline figure stays here; its label is copy.
const STATS: ReadonlyArray<readonly [key: PlatformStatKey, headline: string]> = [
  ["latency", "12ms"],
  ["spread", "0.1"],
  ["uptime", "99.97%"],
  ["liquidity", "Tier 1"],
];

interface FeatureCard {
  key: PlatformFeatureKey;
  Icon: ComponentType;
}

const FEATURES: readonly FeatureCard[] = [
  { key: "algoTrading", Icon: AlgoIcon },
  { key: "depthOfMarket", Icon: DepthIcon },
  { key: "oneClick", Icon: BoltIcon },
  { key: "mobileDesktop", Icon: DevicesIcon },
  { key: "strategyTester", Icon: BeakerIcon },
  { key: "multiAccount", Icon: LayersIcon },
];

// Phase RW (2026-05-07): the old MacBook chassis is replaced by a
// browser-chrome glass-card wrapper showing the same `mt5-back.png`
// screenshot. The 3 floating callouts (Latency / Spread / Uptime)
// that used to overlay the MacBook are dropped — the
// `ExecutionStats` 4-cell strip directly below covers all four
// data points. The browser-chrome window is a simple glass-card
// shell (3 traffic-light dots + URL-bar-style header + body image
// area), composed inline rather than abstracted into a new
// component, since the 3 traffic-light dots are also referenced in
// the mockup but never reused outside this surface (composition
// over premature abstraction).
// Phase RW.16 (2026-05-08): Hero is now a 2-column layout with the
// MT5 browser-chrome screenshot on the right. The previous standalone
// `BrowserChromeShowcase` section was retired — the screenshot used
// to take a full-width slot below the hero, which was visually
// disproportionate (a wide image taking ~600px of vertical space
// just to show a UI). Inlining it into the hero column constrains
// the image to roughly half the container width and shaves a full
// section's worth of height off the page.
function Hero({ t }: { t: PlatformsContent }): JSX.Element {
  const locale = useLocale();
  const chrome = chromeFor(locale);
  return (
    <section className="section-page relative pt-header pb-10 overflow-hidden">
      <div className="brand-glow" aria-hidden="true" />
      <div className="container-page relative z-[2] w-full">
        {/* Top row — copy + bigger browser-chrome screenshot */}
        <div className="grid gap-8 lg:gap-12 items-center lg:grid-cols-[0.9fr_1.15fr]">
          {/* LEFT — copy */}
          <div>
            <h1 className="text-hero font-display font-bold">
              {t.hero.builtOn}
              <br className="md:hidden" />
              <span className="text-brand-gradient">{t.hero.product}</span>
              {t.hero.tail}
              <br />
              {t.hero.line2}
            </h1>
            <p className="text-fg-muted text-[16px] md:text-[18px] max-w-[520px] mt-5">
              {t.hero.lede}
            </p>
            <div className="flex gap-3 mt-7">
              <Button size="lg" variant="primary" href={env.registerUrl}>
                {chrome.cta.getFunded} <ArrowRight />
              </Button>
              <Button size="lg" variant="ghost" href={pathFor("/tradable-assets", locale)}>
                {t.hero.ctaSecondary}
              </Button>
            </div>
          </div>

          {/* RIGHT — bigger browser-chrome screenshot (~54% of grid) */}
          <div className="hidden md:block">
            <Tilt max={3} scale={1.005}>
              <div className="glass-card glass-card-glow p-3.5">
                <div className="flex items-center gap-2 px-2 pb-3 border-b border-border font-mono text-[11px] tracking-[0.14em] text-fg-dim uppercase">
                  <span className="block w-2.5 h-2.5 rounded-full bg-brand" aria-hidden="true" />
                  <span className="block w-2.5 h-2.5 rounded-full bg-warning" aria-hidden="true" />
                  <span className="block w-2.5 h-2.5 rounded-full bg-success" aria-hidden="true" />
                  <span className="ml-3 truncate">METATRADER 5</span>
                  <span className="ml-auto text-brand-emphasis shrink-0">EXAMPLE</span>
                </div>
                <div className="mt-3 rounded-lg overflow-hidden border border-border">
                  <img
                    src="/mt5-back.png"
                    alt="MetaTrader 5 trading platform"
                    className="block w-full"
                    loading="lazy"
                  />
                </div>
              </div>
            </Tilt>
          </div>
        </div>

        {/* Bottom row — execution stats (formerly its own section,
            inlined into the hero so the first viewport carries title,
            image, and the four execution figures together). */}
        <div
          className="mt-10 md:mt-12 glass-card grid gap-px overflow-hidden grid-cols-2 md:grid-cols-4"
          style={{ background: "rgb(var(--border))" }}
        >
          {STATS.map(([statKey, head], i) => {
            // Uptime cell ("99.97%") gets a CountUp — the only stat with
            // a clean numeric headline value worth animating. Latency
            // ("12ms") and spread ("0.1") are too small for count-up
            // to feel meaningful; "Tier 1" is non-numeric.
            //
            // Keyed off the stat's ID, never its label. This branch used to read
            // `label.toLowerCase().startsWith("uptime")`, which is false for the
            // German label — the animation would have silently disappeared in
            // one locale with nothing failing.
            const isUptime = statKey === "uptime";
            return (
              <div key={statKey} className="bg-card px-4 py-4 md:px-6 md:py-5">
                <div
                  className={i === 0 ? "font-display tracking-tight text-brand-gradient leading-none" : "font-display tracking-tight text-fg leading-none"}
                  style={{ fontSize: "clamp(22px, 2.2vw, 32px)", letterSpacing: "-0.02em" }}
                >
                  {isUptime ? (
                    <CountUp to={99.97} duration={1800} format={(n) => `${n.toFixed(2)}%`} />
                  ) : (
                    head
                  )}
                </div>
                <div className="text-[12px] text-fg-muted mt-1.5">{t.stats[statKey]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyMT5({ t }: { t: PlatformsContent }): JSX.Element {
  return (
    <section className="section-y bg-bg-2 border-y border-border">
      <div className="container-page">
        <div className="max-w-[700px]">
          <h2
            className="font-display font-bold"
            style={{
              fontSize: "clamp(24px, 2.4vw, 36px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            {t.why.titleLead}
            <span className="text-brand-gradient">{t.why.titleHighlight}</span>
            {t.why.titleTail}
          </h2>
        </div>
        <RevealStagger className="mt-7 md:mt-9 grid gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-3" step={70}>
          {FEATURES.map((f, i) => (
            <div key={f.key} className="glass-card glass-card-hover p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="font-mono text-[10.5px] text-brand-emphasis tracking-[0.16em]">{String(i + 1).padStart(2, "0")}</div>
                <span className="text-brand-emphasis">
                  <f.Icon />
                </span>
              </div>
              <h3 className="font-display font-semibold mt-2.5" style={{ fontSize: 18, letterSpacing: "-0.01em" }}>{t.features[f.key].title}</h3>
              <p className="text-fg-muted mt-2 text-[13px] leading-[1.55]">{t.features[f.key].desc}</p>
            </div>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}

// Platform-flavoured FAQ deflection, mirroring the /how-it-works
// `FAQRedirect` pattern. Pulls 4 PLATFORM_FAQ_ITEMS and deep-links
// each into the platform's FAQ knowledge base under
// env.traderFaqUrl so the marketing site never duplicates FAQ
// content — admins manage the canonical answers in the platform
// FAQ admin and the slugs are mirrored in `data.ts`.
function PlatformsFAQ({ t }: { t: PlatformsContent }): JSX.Element {
  const previews = PLATFORM_FAQ_ITEMS.slice(0, 4);
  return (
    <section className="section-y-lg">
      <div className="container-page">
        <div className="flex justify-between items-end gap-6 flex-wrap mb-9">
          <SectionHead
            title={
              <>
                {t.faq.titleLead}
                <span className="text-brand-gradient">{t.faq.titleHighlight}</span>
                {t.faq.titleTail}
              </>
            }
            sub={t.faq.sub}
          />
          <Button variant="primary" href={FAQ_BASE_URL}>
            {t.faq.browseAll} <ArrowRight />
          </Button>
        </div>

        <RevealStagger className="glass-card overflow-hidden flex flex-col" step={70}>
          {previews.map((q, i) => {
            const last = i === previews.length - 1;
            return (
              <a
                key={q.key}
                href={`${FAQ_BASE_URL}/${q.path}`}
                className={cx(
                  "flex justify-between items-center gap-4 px-6 py-5 transition-colors hover:bg-card-hover",
                  !last && "border-b border-border",
                )}
              >
                <div>
                  <div className="font-mono text-[10.5px] text-fg-dim tracking-[0.12em]">
                    {t.faq.items[q.key].category}
                  </div>
                  <div className="font-semibold text-[15px] mt-1">{t.faq.items[q.key].q}</div>
                </div>
                <span className="text-fg-muted shrink-0">
                  <ChevronRight />
                </span>
              </a>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
}

export default function PlatformsPage(): JSX.Element {
  const locale = useLocale();
  const t = platformsFor(locale);
  usePageMeta({
    title: t.meta.title,
    description: t.meta.description,
  });
  useBreadcrumbStructuredData([
    { name: "Home", url: "/" },
    { name: "Platforms", url: "/platforms" },
  ]);
  return (
    <>
      <GridBackdrop />
      <div className="animate-page-in relative z-[1]">
        <Hero t={t} />
        <WhyMT5 t={t} />
        <PlatformsFAQ t={t} />
      </div>
    </>
  );
}
